import { useState } from 'react';
import { FiX, FiLayers } from 'react-icons/fi';
import { Modal } from '../../design-system';
import LoadingSpinner from '../common/LoadingSpinner';

// Colores predefinidos para selección rápida
const PRESET_COLORS = [
  '#EF4444', // Rojo
  '#F97316', // Naranja
  '#EAB308', // Amarillo
  '#22C55E', // Verde
  '#14B8A6', // Teal
  '#3B82F6', // Azul
  '#8B5CF6', // Violeta
  '#EC4899', // Rosa
  '#6B7280', // Gris
  '#000000', // Negro
];

const RoomGroupDialog = ({ group, onSave, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: group?.name || '',
    color: group?.color || ''
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!group;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        name: formData.name.trim(),
        color: formData.color || null
      };

      await onSave(dataToSend);
    } catch (error) {
      console.error('Error saving room group:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="md"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FiLayers className="h-5 w-5 text-purple-500" />
            <h3 className="text-lg font-medium text-gray-900">
              {isEditing ? 'Editar Grupo' : 'Nuevo Grupo de Habitaciones'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4" id="room-group-form">
          {/* Name */}
          <div>
            <label htmlFor="name" className="form-label">
              Nombre del grupo *
            </label>
            <input
              id="name"
              type="text"
              className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
              placeholder="Ej: Planta 1, Ala Norte, Edificio A"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              autoFocus
            />
            {errors.name && (
              <p className="mt-1 text-sm text-danger-600">{errors.name}</p>
            )}
          </div>

          {/* Color */}
          <div>
            <label htmlFor="color" className="form-label">
              Color (para cuadrantes por colores)
            </label>
            <div className="space-y-2">
              {/* Preset colors */}
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-lg border-2 transition-all ${
                      formData.color === color
                        ? 'border-gray-900 scale-110 shadow-md'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleChange('color', color)}
                    title={color}
                  />
                ))}
                {/* Clear color button */}
                <button
                  type="button"
                  className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
                    !formData.color
                      ? 'border-gray-900 bg-white'
                      : 'border-gray-300 bg-gray-100 hover:border-gray-400'
                  }`}
                  onClick={() => handleChange('color', '')}
                  title="Sin color"
                >
                  <FiX className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              {/* Custom color picker */}
              <div className="flex items-center space-x-2">
                <input
                  id="color"
                  type="color"
                  className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  value={formData.color || '#808080'}
                  onChange={(e) => handleChange('color', e.target.value)}
                />
                <input
                  type="text"
                  className="form-input flex-1"
                  placeholder="#000000"
                  value={formData.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Este color se usará en los cuadrantes cuando el establecimiento use modo colores
            </p>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Los grupos te permiten organizar las habitaciones en la tabla de órdenes de limpieza.
              Las habitaciones aparecerán agrupadas bajo este nombre.
            </p>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="room-group-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoomGroupDialog;
